import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Alert,
  SafeAreaView,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useSettings } from '../context/SettingsContext';
import { createThemedStyles } from '../theme/useThemedStyles';
import { useTheme } from '../context/ThemeContext';
import { PlatformWrapper, isWeb } from '../utils/platformUtils';
import {
  authenticateWithBiometrics,
  getBiometricErrorMessage,
  hasBiometricHardware,
} from '../utils/biometricAuth';
import { canUseBiometricUnlock, shouldShowPinOnLockScreen } from '../utils/securitySettings';
import WebPinLayout from '../components/web/WebPinLayout';
import BrandWordmark from '../components/BrandWordmark';
import { Ionicons } from '@expo/vector-icons';

interface PinLockScreenProps {
  onUnlock: () => void;
}

const PinLockScreen = ({ onUnlock }: PinLockScreenProps) => {
  const { colors } = useTheme();
  const styles = usePinLockStyles();
  const { settings } = useSettings();
  const [enteredPin, setEnteredPin] = useState('');
  const [attempts, setAttempts] = useState(0);
  const [isBiometricLoading, setIsBiometricLoading] = useState(false);
  const [isUnlocking, setIsUnlocking] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const hasAutoPrompted = useRef(false);
  const MAX_ATTEMPTS = 3;

  const showBiometricUnlock = canUseBiometricUnlock(settings.security);
  const showPinEntry = shouldShowPinOnLockScreen(settings.security);

  useEffect(() => {
    if (!showBiometricUnlock) {
      setBiometricAvailable(false);
      return;
    }

    hasBiometricHardware().then(setBiometricAvailable);
  }, [showBiometricUnlock]);

  const handleBiometricAuth = useCallback(async (userInitiated = false) => {
    if (isBiometricLoading || !showBiometricUnlock) return;

    setIsBiometricLoading(true);

    try {
      const result = await authenticateWithBiometrics('Unlock GoalMint with fingerprint');

      if (result.success) {
        setIsUnlocking(true);
        try {
          await onUnlock();
        } catch (error) {
          console.error('PinLockScreen: Error during biometric unlock:', error);
          setIsUnlocking(false);
        }
      } else {
        const message = getBiometricErrorMessage(result.error, result.warning);
        if (message && userInitiated) {
          Alert.alert('Error', message);
        } else if (message) {
          console.warn('PinLockScreen: Biometric auto-prompt failed:', result.error, result.warning);
        }
      }
    } catch (error) {
      console.error('Biometric auth error:', error);
      if (userInitiated) {
        Alert.alert('Error', 'An error occurred during fingerprint authentication. Please try again.');
      }
    } finally {
      setIsBiometricLoading(false);
    }
  }, [isBiometricLoading, onUnlock, showBiometricUnlock]);

  useEffect(() => {
    if (!showBiometricUnlock || !biometricAvailable || hasAutoPrompted.current) {
      return;
    }

    hasAutoPrompted.current = true;
    const timer = setTimeout(() => {
      handleBiometricAuth(false);
    }, 500);

    return () => clearTimeout(timer);
  }, [showBiometricUnlock, biometricAvailable, handleBiometricAuth]);

  const handlePinSubmit = async () => {
    if (isUnlocking) {
      return;
    }

    if (enteredPin === settings.security.pinCode) {
      setIsUnlocking(true);
      setEnteredPin('');

      try {
        await onUnlock();
      } catch (error) {
        console.error('PinLockScreen: Error during unlock:', error);
        setIsUnlocking(false);
      }
    } else {
      setAttempts((prev) => prev + 1);
      setEnteredPin('');

      if (attempts + 1 >= MAX_ATTEMPTS) {
        Alert.alert('Error', 'Please try again later.');
      } else {
        Alert.alert('Error', `Please try again. ${MAX_ATTEMPTS - (attempts + 1)} attempts remaining.`);
      }
    }
  };

  const pinBody = (
    <>
      {!isWeb && (
        <View style={styles.logoContainer}>
          <Image
            source={require('../../assets/icon.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <BrandWordmark variant="light" size="md" style={styles.appNameContainer} />
        </View>
      )}

      <Text style={[styles.title, isWeb && styles.titleWeb]}>
        {showPinEntry ? 'Enter PIN' : 'Unlock GoalMint'}
      </Text>

      {showPinEntry && (
        <>
          <TextInput
            style={styles.input}
            value={enteredPin}
            onChangeText={setEnteredPin}
            placeholder="••••"
            placeholderTextColor={colors.text.placeholder}
            keyboardType="numeric"
            maxLength={4}
            secureTextEntry
          />
          <TouchableOpacity
            style={[styles.button, isUnlocking && styles.buttonDisabled]}
            onPress={handlePinSubmit}
            disabled={enteredPin.length !== 4 || isUnlocking}
          >
            <Text style={styles.buttonText}>
              {isUnlocking ? 'Unlocking...' : 'Unlock'}
            </Text>
          </TouchableOpacity>
        </>
      )}

      <PlatformWrapper.Native>
        {showBiometricUnlock && biometricAvailable && (
          <TouchableOpacity
            style={[styles.biometricButton, isBiometricLoading && styles.biometricButtonDisabled]}
            onPress={() => handleBiometricAuth(true)}
            disabled={isBiometricLoading}
            activeOpacity={0.5}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            {isBiometricLoading ? (
              <ActivityIndicator size="small" color="white" />
            ) : (
              <Ionicons name="finger-print" size={24} color="white" />
            )}
            <Text style={styles.biometricButtonText}>
              {isBiometricLoading ? 'Authenticating...' : 'Use Fingerprint'}
            </Text>
          </TouchableOpacity>
        )}
      </PlatformWrapper.Native>
    </>
  );

  if (isWeb) {
    return <WebPinLayout>{pinBody}</WebPinLayout>;
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.nav.background }]}>
      <View style={styles.content}>{pinBody}</View>
    </SafeAreaView>
  );
};

const usePinLockStyles = createThemedStyles((colors) => ({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 40,
  },
  logo: {
    width: 100,
    height: 100,
    marginBottom: 16,
  },
  appNameContainer: {
    alignSelf: 'center',
  },
  titleWeb: {
    color: colors.text.primary,
    marginBottom: 24,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: 'white',
    marginBottom: 20,
  },
  input: {
    width: '80%',
    height: 50,
    backgroundColor: colors.surface,
    borderRadius: 8,
    paddingHorizontal: 15,
    fontSize: 24,
    textAlign: 'center',
    marginBottom: 20,
  },
  button: {
    backgroundColor: colors.auth.cta,
    paddingVertical: 12,
    paddingHorizontal: 30,
    borderRadius: 8,
  },
  buttonDisabled: {
    opacity: 0.6,
    backgroundColor: colors.grey[400],
  },
  buttonText: {
    color: colors.auth.ctaText,
    fontSize: 18,
    fontWeight: '500',
  },
  biometricButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    paddingVertical: 15,
    paddingHorizontal: 25,
    marginTop: 20,
    borderWidth: 2,
    borderColor: 'white',
    borderRadius: 12,
    minHeight: 50,
    minWidth: 200,
    justifyContent: 'center',
  },
  biometricButtonDisabled: {
    opacity: 0.6,
    borderColor: colors.grey[400],
  },
  biometricButtonText: {
    color: 'white',
    fontSize: 16,
    marginLeft: 10,
  },
}));

export default PinLockScreen;
